import { Path, classRegistry, type TSVGReviver } from 'fabric';
import draw from 'draw-svg-path';
import { svgPathProperties } from 'svg-path-properties';

const isFirefox =
  typeof window === 'undefined'
    ? false
    : window.navigator.userAgent.toLowerCase().includes('firefox');

const amplitude = 3;
let slavePathEl: SVGPathElement | undefined;

if (typeof window !== 'undefined') {
  const svgEl = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  slavePathEl = document.createElementNS(
    'http://www.w3.org/2000/svg',
    'path',
  ) as SVGPathElement;
  svgEl.append(slavePathEl);
}

// this is basically the same logic as makeSquiggleyLine, but tweaked to be zigzagged instead
const makeZigZag = (stringifiedSvgPath: string): string => {
  let fakeSlavePathEl = {} as ReturnType<typeof svgPathProperties>;

  let pathLen = 0;
  if (isFirefox) {
    fakeSlavePathEl = svgPathProperties(stringifiedSvgPath);
    pathLen = fakeSlavePathEl.getTotalLength();
  } else {
    slavePathEl!.setAttribute('d', stringifiedSvgPath);
    pathLen = slavePathEl!.getTotalLength();
  }

  let pos = isFirefox
    ? fakeSlavePathEl.getPointAtLength(0)
    : slavePathEl!.getPointAtLength(0);
  let newPath = 'M' + [pos.x, pos.y].join(',');
  const numSteps = 2;

  for (let i = 1; i <= numSteps; i++) {
    const last = pos;
    pos = isFirefox
      ? fakeSlavePathEl.getPointAtLength((i * pathLen) / numSteps)
      : slavePathEl!.getPointAtLength((i * pathLen) / numSteps);

    const vector = {
      x: pos.x - last.x,
      y: pos.y - last.y,
    };

    // eslint-disable-next-line unicorn/prefer-modern-math-apis -- keep sqrt(x²+y²) exact; Math.hypot can differ in the last ULP and this length feeds the zigzag SVG geometry (pixel-critical)
    const vectorLen = Math.sqrt(vector.x * vector.x + vector.y * vector.y);

    if (vectorLen === 0) continue;

    const half = {
      x: last.x + vector.x,
      y: last.y + vector.y,
    };

    const perpVector = {
      x: -((amplitude * vector.y) / vectorLen),
      y: (amplitude * vector.x) / vectorLen,
    };

    if (i === numSteps) {
      // last segment before the arrowhead — straighten it so it aligns with the arrow
      newPath += ' ' + [pos.x, pos.y, pos.x, pos.y].join(',');
    } else {
      const controlPoint = {
        x: half.x + perpVector.x,
        y: half.y + perpVector.y,
      };
      newPath += ' ' + [controlPoint.x, controlPoint.y, pos.x, pos.y].join(',');
    }
  }
  return newPath;
};

// Custom Path subclass for the "shot" line type (see makeSquiggleyLine for the subclass/registry pattern).
class ShotLine extends Path {
  static type = 'ShotLine';

  constructor(
    path: ConstructorParameters<typeof Path>[0],
    options?: ConstructorParameters<typeof Path>[1],
  ) {
    super(path, options);
    this.options = options as Record<string, unknown> | undefined;
  }

  toSVG(reviver?: TSVGReviver): string {
    let stringifiedSvgPath = this.path.toString();

    stringifiedSvgPath = stringifiedSvgPath
      .replaceAll('M,', 'M')
      .replaceAll('Q,', 'Q');

    const zigZaggedSvgPath = makeZigZag(stringifiedSvgPath);

    const markup = super.toSVG(reviver);
    const fabricLine = document.createElement('div');
    fabricLine.innerHTML = markup;
    fabricLine.querySelector('path')?.setAttribute('d', zigZaggedSvgPath);
    return fabricLine.innerHTML;
  }

  // eslint-disable-next-line unicorn/prefer-private-class-fields -- `_render` is a Fabric override hook called by name; it cannot be a private field
  _render(ctx: CanvasRenderingContext2D): void {
    let stringifiedSvgPath = this.path.toString();

    stringifiedSvgPath = stringifiedSvgPath
      .replaceAll('M,', 'M')
      .replaceAll('Q,', 'Q');

    const zigZaggedSvgPath = makeZigZag(stringifiedSvgPath);

    ctx.lineWidth = this.options?.strokeWidth as number;
    ctx.strokeStyle = this.options?.stroke as string;
    ctx.beginPath();
    draw(ctx, zigZaggedSvgPath); // replaces new Path2D
    ctx.stroke();
    ctx.closePath();
  }
}

/* eslint-disable unicorn/no-top-level-side-effects -- registering the custom Fabric class + SVG class is the purpose of this module (required for (de)serialisation) */
classRegistry.setClass(ShotLine, 'ShotLine');
classRegistry.setSVGClass(ShotLine, 'ShotLine');
/* eslint-enable unicorn/no-top-level-side-effects */

export default ShotLine;
